/**
 * The quiet inline "Recommended" affordance for a Merlin recommendation on
 * the order (kanban 137dce8a design, 8d83c042 build; owner words, Portal
 * check-in 2026-10-06: "little inline things ... a different colour ...
 * recommended ... hover over that says this email said that this customer
 * should have this exemption or this discount ... and then you can just
 * pick").
 *
 * Three states, placement-agnostic (drops next to any value):
 *   - proposed: a green chip with the recommended value. Click opens ONE
 *     320px popover: the quoted evidence, its source and date, what Accept
 *     does, and Accept / Ignore / Edit. Nothing fires on its own.
 *   - accepted / edited: an 11px grey "from Merlin" mark with the same
 *     popover (what was applied, by whom, when).
 *   - ignored: a faint dot that keeps the popover ("Bring it back").
 *
 * Every click records a portal_events row (recommendation_popover_open /
 * _accepted / _edited / _ignored / _reopened) with the order, recommendation,
 * class and values, so the usage graph and the recon readouts can count
 * them. The parent owns the decision call and the refetch.
 *
 * Not a panel, not a list at the top, nothing without a quoted written
 * source (owner ruling 2026-10-02 on the surcharge advisor panel stands).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { OrderRecommendation, RecommendationDecisionAction } from '../../services/portalOrders';
import { trackEvent } from '../../services/portalTracking';
import styles from './MerlinRecommendationChip.module.css';

interface Props {
  recommendation: OrderRecommendation;
  /** The order the chip sits on (for the audit rows). */
  orderId: string;
  /** False for orders:read viewers: the popover shows the why, no buttons. */
  canEdit: boolean;
  formatPrice: (v: string | number | null | undefined) => string;
  /** The parent performs the call (and refetches). Reject with an Error to show its message. */
  onDecide: (action: RecommendationDecisionAction, appliedValue?: string) => Promise<void>;
}

function parseAmount(raw: string): number | null {
  const cleaned = raw.replace(/[$,\s-]/g, '');
  if (cleaned === '') return null;
  const n = Number(cleaned);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function errorMessage(err: unknown, fallback: string): string {
  const detail =
    typeof err === 'object' && err !== null && 'response' in err
      ? (err as { response?: { data?: { detail?: unknown } } }).response?.data?.detail
      : undefined;
  if (typeof detail === 'string') return detail;
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

const MerlinRecommendationChip: React.FC<Props> = ({ recommendation, orderId, canEdit, formatPrice, onDecide }) => {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState<RecommendationDecisionAction | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The popover hangs off the chip's left edge; when that would run past the
  // viewport (a chip at the right end of a right-aligned value) it hangs off
  // the right edge instead. Measured on open, so the modal's overflow never
  // clips it on either layout of the Freight row.
  const [alignRight, setAlignRight] = useState(false);
  const wrapRef = useRef<HTMLSpanElement>(null);

  const rec = recommendation;
  const audit = {
    order_id: orderId,
    recommendation_id: rec.id,
    exception_class: rec.exception_class,
    recommended_value: rec.recommended_value,
    applied_value: rec.applied_value,
    status: rec.status,
    source_kind: rec.source_kind,
  };

  const close = useCallback(() => {
    setOpen(false);
    setEditing(false);
    setError(null);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (ev: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(ev.target as Node)) close();
    };
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  const toggle = () => {
    if (!open) {
      trackEvent('action', 'recommendation_popover_open', audit);
      const rect = wrapRef.current?.getBoundingClientRect();
      const viewport = typeof window !== 'undefined' ? window.innerWidth : 0;
      setAlignRight(rect != null && viewport > 0 && rect.left + 320 > viewport - 12 && rect.right - 320 >= 0);
    }
    setOpen((v) => !v);
    setEditing(false);
    setError(null);
  };

  const decide = async (action: RecommendationDecisionAction, appliedValue?: string) => {
    setBusy(action);
    setError(null);
    try {
      await onDecide(action, appliedValue);
      trackEvent('action', `recommendation_${action === 'reopen' ? 'reopened' : action === 'accept' ? 'accepted' : action === 'edit' ? 'edited' : 'ignored'}`, {
        ...audit,
        applied_value: appliedValue ?? (action === 'accept' ? rec.recommended_value : rec.applied_value),
      });
      close();
    } catch (err) {
      setError(errorMessage(err, 'That did not go through. Try again.'));
    } finally {
      setBusy(null);
    }
  };

  const submitEdit = () => {
    const n = parseAmount(draft);
    if (n == null) {
      setError('Enter a positive dollar amount.');
      return;
    }
    void decide('edit', n.toFixed(2));
  };

  const decided = rec.status === 'accepted' || rec.status === 'edited';
  const applied = rec.applied_value ?? rec.recommended_value;

  const sourceLine = (
    <div className={styles.source}>
      <b>Source:</b> {rec.source_label}
      {rec.source_author ? ` (${rec.source_author})` : ''}
      {rec.source_date && !rec.source_label.includes(formatDate(rec.source_date)) ? `, ${formatDate(rec.source_date)}` : ''}
    </div>
  );

  const popover = open ? (
    <div
      className={`${styles.popover} ${alignRight ? styles.popoverRight : ''}`}
      role="dialog"
      aria-label="Why this is recommended"
      data-testid="recommendation-popover"
    >
      {rec.status === 'proposed' && (
        <>
          <div className={styles.popHead}>
            <span className={styles.mark}>M</span>
            {rec.label}
            <button
              type="button"
              className={styles.closeX}
              onClick={close}
              aria-label="Close"
              data-action="merlin_recommendation_close"
              data-outcome="close"
              data-target-order-id={orderId}
              data-target-recommendation-id={rec.id}
            >
              ×
            </button>
          </div>
          <div className={styles.quote}>“{rec.quote}”</div>
          {sourceLine}
          {rec.reason && <div className={styles.effect}>{rec.reason}</div>}
          <div className={styles.effect}>
            <b>If accepted:</b> {rec.effect_text}
          </div>
          {canEdit && !editing && (
            <div className={styles.actions}>
              <button
                type="button"
                className={`${styles.smallBtn} ${styles.primaryBtn}`}
                disabled={busy != null}
                onClick={() => void decide('accept')}
                data-testid="recommendation-accept"
              >
                {busy === 'accept' ? 'Writing…' : 'Accept'}
              </button>
              <button
                type="button"
                className={styles.smallBtn}
                disabled={busy != null}
                onClick={() => void decide('ignore')}
                data-testid="recommendation-ignore"
              >
                Ignore
              </button>
              <button
                type="button"
                className={styles.smallBtn}
                disabled={busy != null}
                onClick={() => {
                  setDraft(Number(rec.recommended_value).toFixed(2));
                  setEditing(true);
                }}
                data-testid="recommendation-edit"
              >
                Edit…
              </button>
            </div>
          )}
          {canEdit && editing && (
            <div className={styles.editRow}>
              <span>−$</span>
              <input
                type="text"
                inputMode="decimal"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submitEdit();
                }}
                aria-label="Freight discount amount"
                data-testid="recommendation-edit-amount"
                autoFocus
              />
              <button
                type="button"
                className={`${styles.smallBtn} ${styles.primaryBtn}`}
                disabled={busy != null}
                onClick={submitEdit}
                data-testid="recommendation-edit-apply"
              >
                {busy === 'edit' ? 'Writing…' : 'Apply'}
              </button>
              <button type="button" className={styles.smallBtn} disabled={busy != null} onClick={() => setEditing(false)}>
                Back
              </button>
            </div>
          )}
          {!canEdit && <div className={styles.effect}>Read-only: the order desk decides.</div>}
        </>
      )}
      {decided && (
        <>
          <div className={`${styles.popHead} ${styles.muted}`}>
            <span className={styles.mark}>M</span>
            {rec.status === 'edited' ? 'Edited from Merlin' : 'Accepted from Merlin'}
            <button
              type="button"
              className={styles.closeX}
              onClick={close}
              aria-label="Close"
              data-action="merlin_recommendation_close"
              data-outcome="close"
              data-target-order-id={orderId}
              data-target-recommendation-id={rec.id}
            >
              ×
            </button>
          </div>
          <div>
            Applied −{formatPrice(applied)}
            {rec.status === 'edited' ? ` (recommended −${formatPrice(rec.recommended_value)})` : ''}
            {rec.decided_by_name ? ` by ${rec.decided_by_name}` : ''}
            {rec.decided_at ? ` on ${formatDate(rec.decided_at)}` : ''}.
          </div>
          <div className={styles.quote}>“{rec.quote}”</div>
          {sourceLine}
        </>
      )}
      {rec.status === 'ignored' && (
        <>
          <div className={`${styles.popHead} ${styles.muted}`}>
            <span className={styles.mark}>M</span>
            Ignored
            <button
              type="button"
              className={styles.closeX}
              onClick={close}
              aria-label="Close"
              data-action="merlin_recommendation_close"
              data-outcome="close"
              data-target-order-id={orderId}
              data-target-recommendation-id={rec.id}
            >
              ×
            </button>
          </div>
          <div>
            {rec.label}
            {rec.decided_by_name ? `, ignored by ${rec.decided_by_name}` : ''}
            {rec.decided_at ? ` on ${formatDate(rec.decided_at)}` : ''}.
          </div>
          <div className={styles.quote}>“{rec.quote}”</div>
          {sourceLine}
          {canEdit && (
            <div className={styles.actions}>
              <button
                type="button"
                className={styles.smallBtn}
                disabled={busy != null}
                onClick={() => void decide('reopen')}
                data-testid="recommendation-reopen"
              >
                Bring it back
              </button>
            </div>
          )}
        </>
      )}
      {error && (
        <div className={styles.error} role="alert">
          {error}
        </div>
      )}
    </div>
  ) : null;

  return (
    <span className={styles.wrap} ref={wrapRef} data-testid="merlin-recommendation" data-status={rec.status}>
      {rec.status === 'proposed' && (
        <button
          type="button"
          className={`${styles.chip} ${open ? styles.open : ''}`}
          onClick={toggle}
          title="Why? Click for the source"
          data-testid="recommendation-chip"
        >
          <span className={styles.mark}>M</span>
          {rec.label}
        </button>
      )}
      {decided && (
        <button type="button" className={styles.fromMerlin} onClick={toggle} data-testid="recommendation-from-merlin">
          <span className={styles.mark}>M</span>
          from Merlin
        </button>
      )}
      {rec.status === 'ignored' && (
        <button
          type="button"
          className={styles.ignoredMark}
          onClick={toggle}
          title="Merlin had a recommendation here (ignored)"
          aria-label="Ignored recommendation"
          data-testid="recommendation-ignored-dot"
        >
          ●
        </button>
      )}
      {popover}
    </span>
  );
};

export default MerlinRecommendationChip;
